import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'qs';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TablePagination from '@material-ui/core/TablePagination';
import Checkbox from '@material-ui/core/Checkbox';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import Slide from '@material-ui/core/Slide';
import styled from 'styled-components';
import Search from '../../search/Search';
import {
  getCommand,
  addCheckedCommandId,
  removeCheckedCommandId
} from '../../commandsActions';
import commandState from '../../commandStates';
import { notSentFilter } from '../../search/filters';

import EnhancedTableHead from './EnhancedTableHead';
import CommandTableLoader from './CommandTableLoader';
import CoursePaper from '../../../course/CoursePaper';

const NoCommand = styled.div`
  display: flex;
  justify-content: center;
  margin: 30px 30px;
  font-size: 1.3em;
`;

const styles = theme => ({
  root: {
    width: '100%',
    marginTop: theme.spacing(3)
  },
  table: {
    minWidth: 1020
  },
  tableWrapper: {
    overflowX: 'auto'
  }
});

/**
 * Tableu d'affichage de toutes les commandes dans la partie
 * colis prêt
 * @param {Object} classes                          Classes style
 * @param {{Object[]}} searchCommands                Liste des commandes reçu du serveur
 * @param {Number} numberOfCommands                 Nombre totale de commande
 * @param {Boolean} loading                         Indique si les requêtes sont terminer
 * @param {Function} dispatchGetCommand             Récupération d'une seul commande via sont id
 * @param {Function} dispatchAddCheckedCommandId    Ajouter à la sélection une commande avec sont id
 * @param {Function} dispatchRemoveCheckedCommandId Retire à la sélection une commande avec sont id
 * @param {Array} checkedCommandsIds                Liste des id sélectionné
 * @param {Object} currentCourse                    Si il y a une course, représente la course en cours
 */

const EnhancedTable = ({
  classes,
  searchCommands,
  numberOfCommands,
  loading,
  dispatchGetCommand,
  dispatchAddCheckedCommandId,
  dispatchRemoveCheckedCommandId,
  checkedCommandsIds,
  currentCourse
}) => {
  const { search } = useLocation();
  const history = useHistory();
  const initParams = {
    senderCompany: 'mine'
  };

  const handleChangePage = (event, currentPage) => {
    history.replace({
      search: qs.stringify({
        ...qs.parse(search, { ignoreQueryPrefix: true }),
        page: +currentPage + 1
      })
    });
  };

  return (
    <div>
      <Search
        initParams={initParams}
        filters={notSentFilter}
        mandatoryStates={['waiting_for_courier', 'draft']}
      />
      <Slide
        direction="right"
        in={!!currentCourse}
        mountOnEnter
        unmountOnExit
        timeout={{ enter: 300, exit: 250 }}
      >
        <div style={{ marginTop: 20 }}>
          <CoursePaper />
        </div>
      </Slide>
      <Paper className={classes.root}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <EnhancedTableHead rowCount={numberOfCommands} loading={loading} />
            {loading ? (
              <CommandTableLoader />
            ) : (
              <TableBody>
                {searchCommands.map(command => {
                  const isChecked = checkedCommandsIds.includes(command.id);
                  return (
                    <TableRow
                      hover
                      style={{ cursor: 'pointer' }}
                      role="checkbox"
                      tabIndex={-1}
                      key={command.id}
                      selected={isChecked}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          color="primary"
                          checked={isChecked}
                          onClick={() => {
                            if (isChecked) {
                              return dispatchRemoveCheckedCommandId(command.id);
                            }
                            return dispatchAddCheckedCommandId(command.id);
                          }}
                        />
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => dispatchGetCommand(command.id)}
                      >
                        {`${command.recipientFirstname} ${
                          command.recipientLastname
                        }`}
                      </TableCell>
                      <TableCell
                        align="left"
                        onClick={() => dispatchGetCommand(command.id)}
                      >{`${
                        commandState[command.state]
                          ? commandState[command.state]
                          : 'N/A'
                      }`}</TableCell>
                      <TableCell
                        align="left"
                        onClick={() => dispatchGetCommand(command.id)}
                      >
                        {command.recipientAddress.street}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {!searchCommands.length && !loading && (
            <NoCommand>
              Vous n&lsquo;avez aucune commande pour en ajouter, <br /> cliquer
              sur le boutton importer des commandes
            </NoCommand>
          )}
        </div>
        <TablePagination
          disabled
          rowsPerPageOptions={[10]}
          component="div"
          count={numberOfCommands || 10}
          rowsPerPage={8}
          page={+qs.parse(search, { ignoreQueryPrefix: true }).page - 1}
          backIconButtonProps={{
            disabled:
              loading ||
              +qs.parse(search, { ignoreQueryPrefix: true }).page === 1,
            'aria-label': 'Previous Page'
          }}
          nextIconButtonProps={{
            disabled:
              loading ||
              +qs.parse(search, { ignoreQueryPrefix: true }).page >
                numberOfCommands / 8 - 1,
            'aria-label': 'Next Page'
          }}
          onChangePage={handleChangePage}
        />
      </Paper>
    </div>
  );
};

const mapDispatchToProps = dispatch => ({
  dispatchGetCommand: commandId => dispatch(getCommand(commandId)),
  dispatchAddCheckedCommandId: commandId =>
    dispatch(addCheckedCommandId(commandId)),
  dispatchRemoveCheckedCommandId: commandId =>
    dispatch(removeCheckedCommandId(commandId))
});

const mapStateToProps = state => ({
  searchCommands: state.commands.searchCommands,
  numberOfCommands: state.commands.numberOfCommands,
  loading: state.commands.loading,
  currentCourse: state.init.currentCourse,
  checkedCommandsIds: state.commands.checkedCommandsIds
});

EnhancedTable.propTypes = {
  currentCourse: PropTypes.object,
  classes: PropTypes.object.isRequired,
  searchCommands: PropTypes.array.isRequired,
  numberOfCommands: PropTypes.number,
  dispatchGetCommand: PropTypes.func.isRequired,
  dispatchAddCheckedCommandId: PropTypes.func.isRequired,
  dispatchRemoveCheckedCommandId: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  checkedCommandsIds: PropTypes.array.isRequired
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(EnhancedTable));
